import type { EventDraft } from '../types/events'

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

function parseISODate(iso: string): Date | null {
  if (!ISO_DATE.test(iso)) return null
  const [year, month, day] = iso.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  return Number.isNaN(date.getTime()) ? null : date
}

/** Today's date in the visitor's timezone, as YYYY-MM-DD. */
export function todayISO(): string {
  const now = new Date()
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

export function isValidISODate(iso: string): boolean {
  return parseISODate(iso) !== null
}

/** 'Thursday, August 20' */
export function formatLongDate(iso: string): string {
  const date = parseISODate(iso)
  if (!date) return ''
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })
}

/** { weekday: 'THU', day: '20', month: 'AUG' } */
export function dateMarker(
  iso: string,
): { weekday: string; day: string; month: string } | null {
  const date = parseISODate(iso)
  if (!date) return null
  return {
    weekday: date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase(),
    day: String(date.getDate()),
    month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
  }
}

/** '20:30' → '8:30pm', '21:00' → '9pm' */
export function formatTime(value: string | null | undefined): string | null {
  if (!value) return null
  const match = /^(\d{1,2}):(\d{2})/.exec(value)
  if (!match) return value
  const hours24 = Number(match[1])
  const minutes = match[2]
  const suffix = hours24 >= 12 ? 'pm' : 'am'
  const hours12 = hours24 % 12 || 12
  return minutes === '00' ? `${hours12}${suffix}` : `${hours12}:${minutes}${suffix}`
}

/** 'Doors 8:30pm · Show 9pm' */
export function formatTimes(
  doors: string | null | undefined,
  show: string | null | undefined,
): string | null {
  const parts: string[] = []
  const doorsLabel = formatTime(doors)
  const showLabel = formatTime(show)
  if (doorsLabel) parts.push(`Doors ${doorsLabel}`)
  if (showLabel) parts.push(`Show ${showLabel}`)
  return parts.length > 0 ? parts.join(' · ') : null
}

/** An event stays listed through the whole of its day, then retires itself. */
export function isUpcoming(iso: string, today: string = todayISO()): boolean {
  return isValidISODate(iso) && iso >= today
}

export function sortByDate<T extends Pick<EventDraft, 'eventDate'>>(
  events: T[],
  direction: 'asc' | 'desc' = 'asc',
): T[] {
  const sorted = [...events].sort((a, b) => a.eventDate.localeCompare(b.eventDate))
  return direction === 'asc' ? sorted : sorted.reverse()
}
